import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "pill", "hiddenInput"]
  static values = {
    options: Array,
    selected: { type: Array, default: [] },
    exclude: { type: Array, default: [] }
  }

  connect() {
    this.filteredOptions = this.optionsValue.filter(option =>
      !this.excludeValue.includes(option)
    )
    this.renderDropdown()
    this.renderPills()
    this.addClickOutsideListener()
  }

  disconnect() {
    this.removeClickOutsideListener()
  }

  // Search functionality
  search(event) {
    const query = event.target.value.toLowerCase()
    this.filteredOptions = this.optionsValue.filter(option =>
      option.toLowerCase().includes(query) && !this.excludeValue.includes(option)
    )
    this.renderDropdown()
    // Always show dropdown when searching
    this.dropdownTarget.classList.remove("hidden")
  }

  // Select an option
  selectOption(event) {
    const skill = event.currentTarget.dataset.skill
    if (!this.selectedValue.includes(skill)) {
      this.selectedValue = [...this.selectedValue, skill]
      this.updateHiddenInput()
      this.renderPills()
      this.dispatchSkillsChanged()
    }
    this.inputTarget.value = ""
    this.filteredOptions = [...this.optionsValue]
    this.renderDropdown()
    // Keep dropdown open - don't hide it
    this.inputTarget.focus()
  }

  // Remove a selected skill
  removeSkill(event) {
    event.preventDefault()
    event.stopPropagation()
    const skill = event.currentTarget.dataset.skill
    this.selectedValue = this.selectedValue.filter(s => s !== skill)
    this.updateHiddenInput()
    this.renderPills()
    this.dispatchSkillsChanged()
  }

  // Toggle dropdown visibility
  toggleDropdown() {
    // When focusing, always show dropdown
    this.dropdownTarget.classList.remove("hidden")
  }

  // Add focus/blur listeners
  addClickOutsideListener() {
    this.blurHandler = this.handleBlur.bind(this)
    this.inputTarget.addEventListener('blur', this.blurHandler)
  }

  // Remove focus/blur listeners
  removeClickOutsideListener() {
    if (this.blurHandler) {
      this.inputTarget.removeEventListener('blur', this.blurHandler)
    }
  }

  // Close dropdown when input loses focus (with a small delay to allow for option selection)
  handleBlur(event) {
    // Use setTimeout to allow for option selection before closing
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.dropdownTarget.classList.add("hidden")
      }
    }, 150)
  }

  // Render the dropdown options
  renderDropdown() {
    const availableOptions = this.filteredOptions.filter(option =>
      !this.selectedValue.includes(option) && !this.excludeValue.includes(option)
    )

    this.dropdownTarget.innerHTML = availableOptions.map(option => `
      <div class="px-4 py-2 cursor-pointer text-sm"
           data-skill="${option}"
           data-action="click->searchable-skills#selectOption">
        ${option}
      </div>
    `).join("")
  }

  // Render the pill indicators
  renderPills() {
    const pillsContainer = this.element.querySelector(".pills-container")
    if (!pillsContainer) return

    pillsContainer.innerHTML = this.selectedValue.map(skill => `
      <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-200 text-dk-teal mr-1.5 mb-2">
        ${skill}
  <span
    class="px-2 inline-flex items-center justify-center p-0 leading-none text-dk-teal hover:text-primary focus:outline-none cursor-pointer"
                data-skill="${skill}"
                data-action="click->searchable-skills#removeSkill"
                aria-label="Remove ${skill}">
    <span class="block text-[58px] px-0">×</span>
        </span>
      </span>
    `).join("")
  }

  // Update the hidden input for form submission
  updateHiddenInput() {
    // Simple approach: comma-separated values in a single hidden input
    this.hiddenInputTarget.value = this.selectedValue.join(',')
  }

  // Handle keyboard navigation
  keydown(event) {
    if (event.key === "Escape") {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  // Handle form submission
  beforeSubmit() {
    this.updateHiddenInput()
  }

  // Update the exclude list (called when required skills change)
  updateExcludeList(excludeList) {
    this.excludeValue = excludeList
    this.filteredOptions = this.optionsValue.filter(option =>
      !this.excludeValue.includes(option)
    )
    this.renderDropdown()
  }

  // Dispatch event when skills change
  dispatchSkillsChanged() {
    // Only dispatch if this is the required skills controller
    if (this.element.querySelector('label[for*="required_skills"]')) {
      // Dispatch to document level so other controllers can listen
      const event = new CustomEvent('required-skills-changed', {
        detail: { skills: this.selectedValue },
        bubbles: true
      })
      document.dispatchEvent(event)
    }
  }

  // Handle required skills changed event
  onRequiredSkillsChanged(event) {
    // Only handle if this is the preferred skills controller
    if (this.element.id === "preferred-skills-controller") {
      this.updateExcludeList(event.detail.skills)
    }
  }
}
